import { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { exportFinancialSupportExcel } from './financialSupportExcel';

import {

  Title,

  Text,

  Container,

  Paper,

  ThemeIcon,

  Group,

  Stack,

  TextInput,

  Button,

  Select,

  Badge,

  Table,

  ActionIcon,

  Tooltip,

  Loader,

  Center,

  Modal,

  Divider,

  Card,

  SimpleGrid,

  ScrollArea,

  rem,

  Pagination,

  Textarea,

  NumberInput,

  UnstyledButton,

  FileInput,

} from '@mantine/core';

import { DatePickerInput } from '@mantine/dates';

import {

  IconSearch,

  IconDownload,

  IconFilter,

  IconEye,

  IconArrowLeft,

  IconX,

  IconPencil,

  IconTrash,

  IconDeviceFloppy,

  IconUpload,

  IconSelector,

  IconChevronUp,

  IconChevronDown,

  IconHome,

  IconUser,

  IconCoin,

  IconPhone,

  IconCreditCard,

  IconBuildingBank,

  IconMapPin

} from '@tabler/icons-react';

import { notifications } from '@mantine/notifications';

import api from '../../api/axiosConfig';



const heroGradient =

  'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(16,185,129,0.88) 100%)';



function formatCurrency(value) {

  const num = Number(value || 0);

  return num.toLocaleString('tr-TR', {

    minimumFractionDigits: 2,

    maximumFractionDigits: 2,

  }) + ' TL';

}



function formatDate(value) {

  if (!value) return '-';

  const date = parseDateValue(value);

  if (!date) return String(value);

  return date.toLocaleDateString('tr-TR');

}



function parseDateValue(value) {

  if (!value) return null;

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;



  if (typeof value === 'string') {

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (match) {

      const [, y, m, d] = match;

      return new Date(Number(y), Number(m) - 1, Number(d));

    }

  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;

}



function formatDateForApi(date) {

  if (!date) return null;

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) return null;

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

}



function normalizeText(value) {

  return String(value ?? '').toLowerCase().trim();

}



function safeArray(data) {

  return Array.isArray(data) ? data : [];

}



function toStringOrEmpty(value) {

  return value === null || value === undefined ? '' : String(value);

}



const emptyFamilyForm = {

  id: null,

  tcNo: '',

  name: '',

  startDate: null,

  reference: '',

  bank: '',

  address: '',

  explanation: '',

  phone: '',

  amount: '',

  iban: '',

  status: 'Aktif',

  documents: [],

};



export default function Families() {

  const navigate = useNavigate();



  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [deleting, setDeleting] = useState(false);



  const [data, setData] = useState([]);



  const [searchTerm, setSearchTerm] = useState('');

  const [statusFilter, setStatusFilter] = useState('');

  const [dateRange, setDateRange] = useState([null, null]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });



  const [selectedDocType, setSelectedDocType] = useState('Nüfus Kayıt Örneği');

  const [currentFile, setCurrentFile] = useState(null);



  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);



  const [editModalOpen, setEditModalOpen] = useState(false);

  const [editForm, setEditForm] = useState(emptyFamilyForm);



  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);



  const [page, setPage] = useState(1);

  const [pageSize, setPageSize] = useState(10);



  const fetchData = async () => {

    setLoading(true);

    try {

      const res = await api.get('/financial-support/families/all');

      setData(safeArray(res.data));

    } catch (err) {

      notifications.show({

        title: 'Hata',

        message: 'Aile yardımı verileri yüklenemedi.',

        color: 'red',

      });

    } finally {

      setLoading(false);

    }

  };



  useEffect(() => {

    fetchData();

  }, []);



  useEffect(() => {

    setPage(1);

  }, [searchTerm, statusFilter, dateRange, sortConfig]);



  const filteredData = useMemo(() => {

    const q = normalizeText(searchTerm);

    const start = dateRange?.[0] ? new Date(dateRange[0]) : null;

    const end = dateRange?.[1] ? new Date(dateRange[1]) : null;



    if (start) start.setHours(0, 0, 0, 0);

    if (end) end.setHours(23, 59, 59, 999);



    return data.filter((item) => {

      const name = normalizeText(item?.name);

      const tc = normalizeText(item?.tcNo);

      const reference = normalizeText(item?.reference);

      const address = normalizeText(item?.address);

      const explanation = normalizeText(item?.explanation);



      const matchSearch = !q || name.includes(q) || tc.includes(q) || reference.includes(q) || address.includes(q) || explanation.includes(q);

      const matchStatus = statusFilter ? item?.status === statusFilter : true;



      const itemDate = parseDateValue(item?.startDate);

      const matchDate = (!start && !end) || (itemDate && (!start || itemDate >= start) && (!end || itemDate <= end));



      return matchSearch && matchStatus && matchDate;

    });

  }, [searchTerm, statusFilter, dateRange, data]);



  const sortedData = useMemo(() => {

    let sortableItems = [...filteredData];

    if (sortConfig.key !== null) {

      sortableItems.sort((a, b) => {

        let aValue = a[sortConfig.key];

        let bValue = b[sortConfig.key];



        if (sortConfig.key === 'amount') {

          aValue = Number(aValue || 0);

          bValue = Number(bValue || 0);

        } else if (sortConfig.key === 'startDate') {

          aValue = parseDateValue(aValue)?.getTime() || 0;

          bValue = parseDateValue(bValue)?.getTime() || 0;

        } else {

          aValue = normalizeText(aValue);

          bValue = normalizeText(bValue);

        }



        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;

        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;

        return 0;

      });

    }

    return sortableItems;

  }, [filteredData, sortConfig]);



  const totalCount = filteredData.length;

  const totalBudget = useMemo(() => {

    return filteredData.reduce((acc, curr) => acc + Number(curr?.amount || 0), 0);

  }, [filteredData]);



  const activeCount = useMemo(() => {

    return filteredData.filter((f) => f.status === 'Aktif').length;

  }, [filteredData]);



  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));

  const paginatedData = useMemo(() => {

    const startIndex = (page - 1) * pageSize;

    return sortedData.slice(startIndex, startIndex + pageSize);

  }, [sortedData, page, pageSize]);



  const handleSort = (key) => {

    let direction = 'asc';

    if (sortConfig.key === key && sortConfig.direction === 'asc') {

      direction = 'desc';

    }

    setSortConfig({ key, direction });

  };



  const ThSortable = ({ children, sortKey }) => {

    const isSorted = sortConfig.key === sortKey;

    const ChevronIcon = isSorted ? (sortConfig.direction === 'asc' ? IconChevronUp : IconChevronDown) : IconSelector;

    return (

      <Table.Th style={{ padding: 0 }}>

        <UnstyledButton

          onClick={() => handleSort(sortKey)}

          p="xs"

          w="100%"

          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '4px' }}

        >

          <Text fw={700} size="sm">{children}</Text>

          <ChevronIcon size={14} stroke={1.5} color={isSorted ? 'var(--mantine-color-green-filled)' : 'gray'} />

        </UnstyledButton>

      </Table.Th>

    );

  };



  const clearFilters = () => {

    setSearchTerm('');

    setStatusFilter('');

    setDateRange([null, null]);

    setSortConfig({ key: null, direction: 'asc' });

  };



  const openDetailModal = (row) => {

    setSelectedRow(row);

    setDetailModalOpen(true);

  };



  const openEditModal = (row) => {

    setEditForm({

      id: row?.id ?? null,

      tcNo: toStringOrEmpty(row?.tcNo),

      name: toStringOrEmpty(row?.name),

      startDate: parseDateValue(row?.startDate),

      reference: toStringOrEmpty(row?.reference),

      bank: toStringOrEmpty(row?.bank),

      address: toStringOrEmpty(row?.address),

      explanation: toStringOrEmpty(row?.explanation),

      phone: toStringOrEmpty(row?.phone),

      amount: toStringOrEmpty(row?.amount),

      iban: toStringOrEmpty(row?.iban),

      status: row?.status ?? 'Aktif',

      documents: row?.documents ?? [],

    });

    setSelectedRow(row);

    setEditModalOpen(true);

  };



  const openDeleteModal = (row) => {

    setDeleteTarget(row);

    setDeleteModalOpen(true);

  };



  const downloadPdf = async (url, fileName) => {

    if (!url) return;

    try {

      const response = await api.get(url, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });

      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = downloadUrl;

      link.download = fileName;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(downloadUrl);

    } catch (err) {

      notifications.show({ title: 'Hata', message: 'Belge indirilemedi.', color: 'red' });

    }

  };



  const handleAddDocumentToForm = () => {

    if (!currentFile) return;

    const newDoc = {

      type: selectedDocType,

      name: currentFile.name,

      file: currentFile 

    };

    setEditForm(prev => ({

      ...prev,

      documents: [...(prev.documents || []), newDoc]

    }));

    setCurrentFile(null); 

  };



  const handleRemoveDocumentFromForm = (indexToRemove) => {

    setEditForm(prev => ({

      ...prev,

      documents: prev.documents.filter((_, idx) => idx !== indexToRemove)

    }));

  };



  const handleEditSubmit = async () => {

    // TC, Adres, Telefon zorunluluğu kaldırıldı. Sadece İsim ve Tutar zorunlu.

    if (!editForm.name || !editForm.amount) {

      notifications.show({

        title: 'Eksik Alan',

        message: 'Aile/Temsilci Adı ve Yardım Tutarı zorunludur.',

        color: 'red',

      });

      return;

    }



    setSaving(true);

    try {

      const formDataPayload = new FormData();

      formDataPayload.append('tcNo', editForm.tcNo || '');

      formDataPayload.append('name', editForm.name);

      formDataPayload.append('phone', editForm.phone || '');

      formDataPayload.append('amount', String(editForm.amount));

      formDataPayload.append('bank', editForm.bank || '');

      formDataPayload.append('iban', editForm.iban || '');

      formDataPayload.append('reference', editForm.reference || '');

      formDataPayload.append('address', editForm.address || '');

      formDataPayload.append('explanation', editForm.explanation || '');

      formDataPayload.append('status', editForm.status);



      if (editForm.startDate) {

        formDataPayload.append('startDate', formatDateForApi(editForm.startDate));

      }



      // Dosya Ayırma Mantığı (Mevcut olanlar vs Yeni Eklenenler)

      const existingDocs = editForm.documents.filter(d => d.id); 

      const newDocs = editForm.documents.filter(d => !d.id);    



      // Backend'e kalmasını istediğimiz mevcut dosyaların ID'lerini bildiriyoruz

      formDataPayload.append('existing_document_ids', JSON.stringify(existingDocs.map(d => d.id)));



      // Sadece yeni dosyaları sunucuya iletiyoruz

      if (newDocs.length > 0) {

        newDocs.forEach((doc) => {

          if (doc.file) { 

            formDataPayload.append('files', doc.file);

            formDataPayload.append('doc_types', doc.type);

          }

        });

      }



      let res;

      if (editForm.id) {

        res = await api.patch(`/financial-support/families/${editForm.id}`, formDataPayload, {

          headers: { 'Content-Type': 'multipart/form-data' }

        });

        setData((prev) => prev.map((item) => (item.id === res.data.id ? res.data : item)));

        notifications.show({ title: 'Başarılı', message: 'Aile yardım kaydı güncellendi.', color: 'teal' });

      } else {

        res = await api.post('/financial-support/families/create', formDataPayload, {

          headers: { 'Content-Type': 'multipart/form-data' }

        });

        setData((prev) => [res.data, ...prev]);

        notifications.show({ title: 'Başarılı', message: 'Yeni aile yardım kaydı oluşturuldu.', color: 'teal' });

      }

      setEditModalOpen(false);

    } catch (err) {

      notifications.show({

        title: 'Hata',

        message: err?.response?.data?.detail || 'İşlem gerçekleştirilemedi.',

        color: 'red',

      });

    } finally {

      setSaving(false);

    }

  };



  const handleDelete = async () => {

    if (!deleteTarget?.id) return;

    setDeleting(true);

    try {

      await api.delete(`/financial-support/families/${deleteTarget.id}`);

      setData((prev) => prev.filter((item) => item.id !== deleteTarget.id));

      notifications.show({ title: 'Başarılı', message: 'Aile kaydı silindi.', color: 'teal' });

      setDeleteModalOpen(false);

    } catch (err) {

      notifications.show({ title: 'Hata', message: 'Kayıt silinemedi.', color: 'red' });

    } finally {

      setDeleting(false);

    }

  };



  const handleExportExcel = async () => {
    if (sortedData.length === 0) {
      notifications.show({ title: 'Bilgi', message: 'Excel raporu için listede kayıt bulunamadı.', color: 'yellow' });
      return;
    }

    try {
      await exportFinancialSupportExcel({
        kind: 'family',
        rows: sortedData,
        filters: {
          searchTerm,
          statusFilter,
          dateRange,
        },
      });
      notifications.show({
        title: 'Excel raporu oluşturuldu',
        message: 'Kurumsal tasarımlı Excel dosyası indirildi.',
        color: 'teal',
      });
    } catch (error) {
      console.error('Excel raporu oluşturulamadı:', error);
      notifications.show({
        title: 'Excel raporu oluşturulamadı',
        message: error?.message || 'Lütfen tekrar deneyin.',
        color: 'red',
      });
    }
  };



  return (

    <Container fluid p={{ base: 'sm', md: 'lg' }}>

      <Stack gap="xl">



        {/* HERO HEADER PANEL */}

        <Paper

          radius="xl"

          p={{ base: 'lg', md: 'xl' }}

          style={{

            background: heroGradient,

            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',

            border: '1px solid rgba(255,255,255,0.08)'

          }}

        >

          <Stack gap="md">

            <Group justify="space-between" align="flex-start">

              <Button

                variant="white"

                radius="xl"

                size="sm"

                leftSection={<IconArrowLeft size={16} />}

                onClick={() => navigate('/financial-support')}

                style={{ background: 'rgba(255,255,255,0.14)', color: 'white', border: 'none' }}

              >

                Ana Menüye Dön

              </Button>

              <Group gap="xs">

                <Badge variant="light" color="cyan" radius="xl">{totalCount} Hane</Badge>

                <Badge variant="light" color="teal" radius="xl">{formatCurrency(totalBudget)} Aylık Fon</Badge>

              </Group>

            </Group>



            <Group gap="sm">

              <ThemeIcon size={42} radius="xl" variant="white" color="green">

                <IconHome size={22} />

              </ThemeIcon>

              <Stack gap={2}>

                <Text size="sm" fw={600} c="rgba(255,255,255,0.8)">Portal Modülleri</Text>

                <Title order={1} fw={900} c="white">Aile Sosyal Yardım Takip Sistemi</Title>

              </Stack>

            </Group>

            <Text c="rgba(255,255,255,0.78)" size="sm">

              Yardım sağlanan ailelerin mali verilerini, sorumlu veli/temsilci bilgilerini ve sosyal durum evraklarını merkezi olarak denetleyin.

            </Text>

          </Stack>

        </Paper>



        {/* METRİK KARTLARI */}

        <SimpleGrid cols={{ base: 1, sm: 3 }}>

          <Paper radius="xl" p="lg" withBorder shadow="xs">

            <Group justify="space-between">

              <Stack gap={4}>

                <Text size="sm" c="dimmed">Toplam Desteklenen Hane</Text>

                <Text fw={900} size={rem(28)}>{totalCount}</Text>

              </Stack>

              <ThemeIcon radius="xl" variant="light" color="green" size={42}><IconHome size={20} /></ThemeIcon>

            </Group>

          </Paper>

          <Paper radius="xl" p="lg" withBorder shadow="xs">

            <Group justify="space-between">

              <Stack gap={4}>

                <Text size="sm" c="dimmed">Aktif Ödeme Alan Haneler</Text>

                <Text fw={900} size={rem(28)}>{activeCount} Aile</Text>

              </Stack>

              <ThemeIcon radius="xl" variant="light" color="teal" size={42}><IconUser size={20} /></ThemeIcon>

            </Group>

          </Paper>

          <Paper radius="xl" p="lg" withBorder shadow="xs">

            <Group justify="space-between">

              <Stack gap={4}>

                <Text size="sm" c="dimmed">Aylık Toplam Sosyal Yardım</Text>

                <Text fw={900} size={rem(26)}>{formatCurrency(totalBudget)}</Text>

              </Stack>

              <ThemeIcon radius="xl" variant="light" color="emerald" size={42}><IconCoin size={20} /></ThemeIcon>

            </Group>

          </Paper>

        </SimpleGrid>



        {/* TABLO VE FİLTRELER */}

        <Paper radius="xl" p={{ base: 'md', md: 'xl' }} withBorder shadow="sm">

          <Stack gap="lg">

            <Group justify="space-between" align="flex-end">

              <Stack gap={4}>

                <Group gap="xs"><IconFilter size={18} /><Text fw={700}>Arama ve Filtreleme</Text></Group>

              </Stack>

              <Group gap="sm">

                <Button leftSection={<IconX size={16} />} radius="xl" variant="light" color="gray" onClick={clearFilters}>

                  Filtreleri Temizle

                </Button>

                <Button leftSection={<IconDownload size={18} />} radius="xl" color="green" variant="light" onClick={handleExportExcel}>

                  Excel&apos;e Aktar

                </Button>

                <Button color="green" radius="xl" onClick={() => { setEditForm(emptyFamilyForm); setEditModalOpen(true); }}>

                  + Yeni Aile Ekle

                </Button>

              </Group>

            </Group>



            <SimpleGrid cols={{ base: 1, md: 3 }}>

              <TextInput label="Hızlı Arama" placeholder="Aile Temsilcisi, TC No, Referans..." leftSection={<IconSearch size={16} />} value={searchTerm} onChange={(e) => setSearchTerm(e.currentTarget.value)} radius="md" />

              <Select label="Durum" placeholder="Tümü" data={['Aktif', 'Pasif', 'Beklemede']} value={statusFilter} onChange={setStatusFilter} radius="md" clearable />

              <DatePickerInput type="range" label="Yardım Başlangıç Tarihi Aralığı" placeholder="Seçim Yapın" value={dateRange} onChange={setDateRange} valueFormat="DD.MM.YYYY" radius="md" clearable />

            </SimpleGrid>



            <Divider />



            {loading ? (

              <Center py="xl"><Loader size="lg" /></Center>

            ) : sortedData.length === 0 ? (

              <Card withBorder radius="xl" p="xl">

                <Center><Text fw={500} c="dimmed">Sistemde kriterlere uygun aile yardım kaydı bulunamadı.</Text></Center>

              </Card>

            ) : (

              <>

                <ScrollArea>

                  <Table verticalSpacing="md" highlightOnHover striped withTableBorder>

                    <Table.Thead bg="var(--mantine-color-gray-0)">

                      <Table.Tr>

                        <ThSortable sortKey="tcNo">TC No</ThSortable>

                        <ThSortable sortKey="name">Aile / Temsilci Adı</ThSortable>

                        <Table.Th><Text fw={700} size="sm">Telefon</Text></Table.Th>

                        <Table.Th><Text fw={700} size="sm">Referans</Text></Table.Th>

                        <ThSortable sortKey="amount">Destek Tutarı</ThSortable>

                        <ThSortable sortKey="startDate">Başlangıç</ThSortable>

                        <Table.Th><Text fw={700} size="sm">Durum</Text></Table.Th>

                        <Table.Th style={{ textAlign: 'right' }}><Text fw={700} size="sm">İşlemler</Text></Table.Th>

                      </Table.Tr>

                    </Table.Thead>

                    <Table.Tbody>

                      {paginatedData.map((row) => (

                        <Table.Tr key={row.id}>

                          <Table.Td>{row.tcNo}</Table.Td>

                          <Table.Td fw={600}>{row.name}</Table.Td>

                          <Table.Td>{row.phone}</Table.Td>

                          <Table.Td><Text size="sm" lineClamp={1}>{row.reference || '-'}</Text></Table.Td>

                          <Table.Td fw={700} c="green">{formatCurrency(row.amount)}</Table.Td>

                          <Table.Td>{formatDate(row.startDate)}</Table.Td>

                          <Table.Td>

                            <Badge color={row.status === 'Aktif' ? 'green' : row.status === 'Beklemede' ? 'yellow' : 'red'} variant="light">

                              {row.status}

                            </Badge>

                          </Table.Td>

                          <Table.Td style={{ textAlign: 'right' }}>

                            <Group gap="xs" justify="flex-end">

                              <Tooltip label="Detay Gör"><ActionIcon variant="light" color="blue" onClick={() => openDetailModal(row)}><IconEye size={18} /></ActionIcon></Tooltip>

                              <Tooltip label="Düzenle"><ActionIcon variant="light" color="orange" onClick={() => openEditModal(row)}><IconPencil size={18} /></ActionIcon></Tooltip>

                              <Tooltip label="Sil"><ActionIcon variant="light" color="red" onClick={() => openDeleteModal(row)}><IconTrash size={18} /></ActionIcon></Tooltip>

                            </Group>

                          </Table.Td>

                        </Table.Tr>

                      ))}

                    </Table.Tbody>

                  </Table>

                </ScrollArea>



                <Group justify="space-between" align="center" mt="lg">

                  <Group gap="sm">

                    <Select

                      data={[{ value: '10', label: '10 Kayıt' }, { value: '25', label: '25 Kayıt' }, { value: '50', label: '50 Kayıt' }]}

                      value={String(pageSize)}

                      onChange={(val) => { setPageSize(Number(val)); setPage(1); }}

                      radius="xl" w={120}

                    />

                    <Text size="sm" c="dimmed">Toplam {sortedData.length} kayıttan {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, sortedData.length)} arası gösteriliyor</Text>

                  </Group>

                  <Pagination value={page} onChange={setPage} total={totalPages} radius="xl" withEdges color="green" />

                </Group>

              </>

            )}

          </Stack>

        </Paper>

      </Stack>



      {/* MODAL: SOSYAL YARDIM DETAYI */}

      <Modal opened={detailModalOpen} onClose={() => setDetailModalOpen(false)} title="📋 Aile Sosyal Yardım Kartı" radius="xl" centered size="lg">

        {selectedRow && (

          <Stack gap="md">

            <Paper radius="lg" p="md" withBorder>

              <Group justify="space-between">

                <Stack gap={2}>

                  <Text size="xs" c="dimmed">SORUMLU KİŞİ TC / AİLE ADI</Text>

                  <Text fw={800} size="lg">{selectedRow.name} {selectedRow.tcNo ? `(${selectedRow.tcNo})` : ''}</Text>

                </Stack>

                <Badge size="lg" color={selectedRow.status === 'Aktif' ? 'green' : 'red'} variant="light">{selectedRow.status}</Badge>

              </Group>

            </Paper>



            <SimpleGrid cols={{ base: 1, sm: 2 }}>

              <Paper withBorder radius="lg" p="md">

                <Group gap="sm"><ThemeIcon variant="light" color="green" radius="xl"><IconCoin size={18} /></ThemeIcon>

                  <Stack gap={2}><Text size="xs" c="dimmed">AYLIK DESTEK TUTARI</Text><Text fw={600} c="green">{formatCurrency(selectedRow.amount)}</Text></Stack>

                </Group>

              </Paper>

              <Paper withBorder radius="lg" p="md">

                <Group gap="sm"><ThemeIcon variant="light" color="orange" radius="xl"><IconPhone size={18} /></ThemeIcon>

                  <Stack gap={2}><Text size="xs" c="dimmed">İLETİŞİM TELEFONU</Text><Text fw={600}>{selectedRow.phone || '-'}</Text></Stack>

                </Group>

              </Paper>

              <Paper withBorder radius="lg" p="md">

                <Group gap="sm"><ThemeIcon variant="light" color="teal" radius="xl"><IconUser size={18} /></ThemeIcon>

                  <Stack gap={2}><Text size="xs" c="dimmed">REFERANS / KURUM</Text><Text fw={600}>{selectedRow.reference || '-'}</Text></Stack>

                </Group>

              </Paper>

              <Paper withBorder radius="lg" p="md">

                <Group gap="sm"><ThemeIcon variant="light" color="violet" radius="xl"><IconBuildingBank size={18} /></ThemeIcon>

                  <Stack gap={2}><Text size="xs" c="dimmed">ÖDEME YAPILACAK BANKA</Text><Text fw={600}>{selectedRow.bank || '-'}</Text></Stack>

                </Group>

              </Paper>

            </SimpleGrid>



            <Paper withBorder radius="lg" p="md">

              <Group gap="sm"><ThemeIcon variant="light" color="indigo" radius="xl"><IconCreditCard size={18} /></ThemeIcon>

                <Stack gap={2}><Text size="xs" c="dimmed">HESAP IBAN NUMARASI</Text><Text fw={600} style={{ fontFamily: 'monospace' }}>{selectedRow.iban || '-'}</Text></Stack>

              </Group>

            </Paper>



            <Paper withBorder radius="lg" p="md">

              <Group gap="sm"><ThemeIcon variant="light" color="red" radius="xl"><IconMapPin size={18} /></ThemeIcon>

                <Stack gap={2}><Text size="xs" c="dimmed">İKAMETGAH ADRESİ</Text><Text size="sm">{selectedRow.address || '-'}</Text></Stack>

              </Group>

            </Paper>

            <Paper withBorder radius="lg" p="md">

              <Group gap="sm"><ThemeIcon variant="light" color="red" radius="xl"><IconMapPin size={18} /></ThemeIcon>

                <Stack gap={2}><Text size="xs" c="dimmed">AÇIKLAMA</Text><Text size="sm">{selectedRow.explanation || '-'}</Text></Stack>

              </Group>

            </Paper>



            {/* EVRAK LİSTELEME PANELDİR */}

            <Paper withBorder radius="lg" p="md">

              <Stack gap="sm">

                <Text size="xs" c="dimmed" tt="uppercase">📁 Ekli Sosyal İnceleme Evrakları</Text>

                {selectedRow.documents && selectedRow.documents.length > 0 ? (

                  <Stack gap="xs">

                    {selectedRow.documents.map((doc, idx) => (

                      <Group key={idx} justify="space-between" p="xs" bg="var(--mantine-color-gray-0)" style={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}>

                        <Group gap="xs">

                          <Badge color="teal" variant="outline">{doc.type}</Badge>

                          <Text size="sm">{doc.name}</Text>

                        </Group>

                        <Button size="xs" variant="subtle" color="green" onClick={() => downloadPdf(doc.url, doc.name)}>İndir</Button>

                      </Group>

                    ))}

                  </Stack>

                ) : (

                  <Text size="sm" c="dimmed" fs="italic">Sisteme yüklenmiş herhangi bir inceleme belgesi bulunamadı.</Text>

                )}

              </Stack>

            </Paper>



            <Group justify="space-between" mt="md">

              <Button variant="light" color="orange" radius="xl" onClick={() => { setDetailModalOpen(false); openEditModal(selectedRow); }}>Düzenle</Button>

              <Button variant="light" color="red" radius="xl" onClick={() => { setDetailModalOpen(false); openDeleteModal(selectedRow); }}>Sil</Button>

              <Button variant="default" radius="xl" onClick={() => setDetailModalOpen(false)}>Kapat</Button>

            </Group>

          </Stack>

        )}

      </Modal>



      {/* MODAL: EKLEME VE DÜZENLEME FORMU */}

      <Modal opened={editModalOpen} onClose={() => setEditModalOpen(false)} title={editForm.id ? "Aile Yardım Verilerini Düzenle" : "Yeni Aile Yardım Kaydı Ekle"} radius="xl" centered size="lg">

        <Stack gap="md">

          <SimpleGrid cols={{ base: 1, sm: 2 }}>

            <TextInput 

              label="T.C. Kimlik No" 

              value={editForm.tcNo} 

              onChange={(e) => {

                const val = e.target.value.replace(/[^0-9]/g, '');

                setEditForm(p => ({ ...p, tcNo: val }));

              }} 

              radius="md" 

              maxLength={11} 

            />

            <TextInput 

              label="Aile / Temsilci Adı" 

              value={editForm.name} 

              onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} 

              radius="md" 

              required 

              placeholder="Örn: Yılmaz Ailesi (Fatma Yılmaz)" 

            />

            <TextInput 

              label="Telefon" 

              value={editForm.phone} 

              onChange={(e) => {

                const val = e.target.value.replace(/[^0-9]/g, '');

                setEditForm(p => ({ ...p, phone: val }));

              }} 

              radius="md" 

            />

            <DatePickerInput label="Yardım Başlangıç Tarihi" value={editForm.startDate} onChange={(val) => setEditForm(p => ({ ...p, startDate: val }))} radius="md" valueFormat="DD.MM.YYYY" />

            <NumberInput label="Aylık Yardım Tutarı (TL)" value={editForm.amount} onChange={(val) => setEditForm(p => ({ ...p, amount: val }))} radius="md" min={0} required />



            <Select 

              label="Banka Adı" 

              data={['Ziraat Bankası', 'Halkbank', 'Vakıfbank', 'İş Bankası', 'Garanti BBVA', 'Akbank', 'Yapı Kredi', 'QNB Finansbank', 'Kuveyt Türk', 'DenizBank', 'TEB', 'Diğer']} 

              value={editForm.bank} 

              onChange={(val) => setEditForm(p => ({ ...p, bank: val || '' }))} 

              radius="md" 

              searchable

              clearable

            />



            <TextInput label="Durum" value={editForm.status} onChange={(e) => setEditForm(p => ({ ...p, status: e.target.value }))} radius="md" />



            <TextInput label="Referans / Kurum" value={editForm.reference} onChange={(e) => setEditForm(p => ({ ...p, reference: e.target.value }))} radius="md" />

          </SimpleGrid>



          <TextInput label="IBAN Numarası" value={editForm.iban} onChange={(e) => setEditForm(p => ({ ...p, iban: e.target.value }))} radius="md" placeholder="TR..." />

          <Textarea label="İkametgah Adresi" value={editForm.address} onChange={(e) => setEditForm(p => ({ ...p, address: e.target.value }))} radius="md" minRows={2} />

          <Textarea label="Açıklama" value={editForm.explanation} onChange={(e) => setEditForm(p => ({ ...p, explanation: e.target.value }))} radius="md" minRows={2} />



          {/* EVRAK YÜKLEME ALANI */}

          <Paper withBorder radius="lg" p="md" bg="var(--mantine-color-gray-0)">

            <Stack gap="xs">

              <Text size="sm" fw={700}>Gerekli Sosyal İnceleme Evrakı Ekle</Text>

              <SimpleGrid cols={{ base: 1, sm: 3 }} align="end">

                <Select 

                  label="Belge Türü" 

                  data={['Nüfus Kayıt Örneği', 'İkametgah Belgesi', 'Diğer']} 

                  value={selectedDocType} 

                  onChange={(val) => setSelectedDocType(val || 'Nüfus Kayıt Örneği')} 

                  radius="md" 

                />

                <FileInput 

                  label="Dosya Seç" 

                  placeholder="PDF/Görsel iliştirin" 

                  leftSection={<IconUpload size={16} />} 

                  value={currentFile} 

                  onChange={setCurrentFile} 

                  radius="md" 

                />

                <Button color="green" radius="md" onClick={handleAddDocumentToForm} disabled={!currentFile}>

                  Evrak Ekle

                </Button>

              </SimpleGrid>



              {editForm.documents?.length > 0 && (

                <Stack gap="xs" mt="sm">

                  <Text size="xs" fw={600} c="dimmed">Yüklenecek / Mevcut Belgeler:</Text>

                  {editForm.documents.map((doc, idx) => (

                    <Group key={idx} justify="space-between" p="xs" bg="white" style={{ borderRadius: '6px', border: '1px solid #e2e8f0' }}>

                      <Group gap="xs">

                        <Badge variant="light" color="green">{doc.type}</Badge>

                        <Text size="sm" lineClamp={1}>{doc.name}</Text>

                      </Group>

                      <ActionIcon color="red" variant="subtle" onClick={() => handleRemoveDocumentFromForm(idx)}>

                        <IconX size={16} />

                      </ActionIcon>

                    </Group>

                  ))}

                </Stack>

              )}

            </Stack>

          </Paper>



          <Group justify="flex-end" mt="md">

            <Button variant="default" radius="xl" onClick={() => setEditModalOpen(false)}>Vazgeç</Button>

            <Button color="green" leftSection={<IconDeviceFloppy size={16} />} radius="xl" onClick={handleEditSubmit} loading={saving}>Kaydet</Button>

          </Group>

        </Stack>

      </Modal>



      {/* MODAL: SİLME ONAYI */}

      <Modal opened={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Kayıt Silme İşlemi" radius="xl" centered size="md">

        <Stack gap="md">

          <Text><strong>{deleteTarget?.name}</strong> yardım kaydını silmek üzeresiniz. Bu işlem veritabanından kalıcı olarak silinecektir ve geri alınamaz.</Text>

          <Group justify="flex-end">

            <Button variant="default" radius="xl" onClick={() => setDeleteModalOpen(false)}>İptal</Button>

            <Button color="red" radius="xl" leftSection={<IconTrash size={16} />} loading={deleting} onClick={handleDelete}>Kalıcı Olarak Sil</Button>

          </Group>

        </Stack>

      </Modal>



    </Container>

  );

}
